import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useCompany } from "../../../contexts/CompanyContext";
import {
  bankAccountsApi,
  costCentersApi,
  subcategoriesApi,
  transactionsApi,
} from "../../../services";
import type {
  BankAccountAttributes,
  CostCenterAttributes,
  DashboardCategoryRow,
  DashboardGroupRow,
  DashboardTransaction,
  SubcategoryAttributes,
  TransactionAttributes,
  TransactionDashboardData,
} from "../../../types";
import { formatCurrency, formatDate } from "../../../utils/formatUtils";
import {
  ActionButton,
  ErrorState,
  Icons,
  IconsLarge,
  Input,
  Loading,
  Modal,
  useToaster,
} from "../../shared";
import { CreateInputTransactionModal } from "../Transaction/CreateInputTransactionModal";
import { CreateTransactionModal } from "../Transaction/CreateTransactionModal";
import {
  computeDerivedDashboard,
  formatMonthKey,
  getCategoryTransactions,
  isEntradasPlan,
  totalForMonth,
  transactionsForCategoryMonth,
  transactionsForGroupMonth,
  transactionsForMonthInDashboard,
  transactionsForSubcategoryMonth,
} from "../../../utils/demonstrativoDashboardDerived";
import { competenceDateToMonthValue } from "../../../utils/competenceDateUtils";
import {
  competenceRangeFromCustomMonths,
  competenceRangeFromPreset,
  DASHBOARD_PERIOD_PRESETS,
  type DashboardCompetencePreset,
} from "../../../utils/dashboardCompetenceRangeUtils";

/** Como exibir o valor: só Entradas em verde (+); saídas sempre vermelho (−); totais líquidos seguem o sinal. */
type ValuePresentation = "input" | "output" | "net";

function formatSignedCell(
  value: number,
  presentation: ValuePresentation = "net"
): { text: string; className: string } {
  if (value === 0 || Number.isNaN(value)) {
    return { text: "—", className: "text-gray-400 dark:text-slate-500" };
  }
  if (presentation === "input") {
    const abs = formatCurrency(Math.abs(value));
    return { text: `+ ${abs}`, className: "text-emerald-600 dark:text-emerald-400" };
  }
  if (presentation === "output") {
    const abs = formatCurrency(Math.abs(value));
    return { text: `- ${abs}`, className: "text-red-600 dark:text-red-400" };
  }
  const abs = formatCurrency(Math.abs(value));
  if (value > 0) {
    return { text: `+ ${abs}`, className: "text-emerald-600 dark:text-emerald-400" };
  }
  return { text: `- ${abs}`, className: "text-red-600 dark:text-red-400" };
}

function renderSummaryMonthCells(
  monthKeys: string[],
  values: number[],
  entradas: number[]
): React.ReactNode[] {
  return monthKeys.map((mk, i) => {
    const v = values[i] ?? 0;
    const e = entradas[i] ?? 0;
    const { text, className } = formatSignedCell(v, "net");
    const den = Math.abs(e);
    let pctLine: React.ReactNode = null;
    if (den >= 1e-9) {
      const pct = (v / den) * 100;
      const pctSign = pct > 0 ? "+" : "";
      pctLine = (
        <span className="block text-[11px] font-normal text-gray-500 dark:text-slate-400 mt-0.5">
          {pctSign}
          {Math.round(pct)}% das entradas
        </span>
      );
    }
    return (
      <td
        key={mk}
        className="px-3 py-2 text-right text-sm tabular-nums whitespace-nowrap align-top border-b border-gray-200 dark:border-[#374151]"
      >
        <span className={className}>{text}</span>
        {pctLine}
      </td>
    );
  });
}

type RenderMonthCellsFn = (
  totals: { month: string; totalAmount: number }[],
  presentation: "input" | "output",
  onMonthClick?: (monthKey: string) => void
) => React.ReactNode[];

type DemonstrativoSeparatePlanCardProps = {
  group: DashboardGroupRow;
  monthKeys: string[];
  presentation: "input" | "output";
  renderMonthCells: RenderMonthCellsFn;
  /** Clique no cabeçalho do mês (todos os lançamentos do demonstrativo na competência). */
  onTableHeaderMonthClick?: (monthKey: string) => void;
  /** Clique na célula de mês da linha do plano (apenas este plano). */
  onPlanRowMonthClick: (monthKey: string) => void;
  children: React.ReactNode;
};

/** Mesmo bloco visual da grade principal (cabeçalho + linha do plano expansível + categorias). */
const DemonstrativoSeparatePlanCard: React.FC<DemonstrativoSeparatePlanCardProps> = ({
  group,
  monthKeys,
  presentation,
  renderMonthCells,
  onTableHeaderMonthClick,
  onPlanRowMonthClick,
  children,
}) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-gray-200 dark:border-[#374151] bg-white dark:bg-[#1A202C] shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-max w-full border-collapse text-left">
          <thead>
            <tr className="bg-gray-50 dark:bg-[#2D3748]">
              <th className="sticky left-0 z-10 bg-gray-50 dark:bg-[#2D3748] px-3 py-2 text-xs font-semibold text-gray-600 dark:text-slate-300 border-b border-gray-200 dark:border-[#374151] min-w-[220px]">
                Plano / Categoria
              </th>
              {monthKeys.map((mk) => (
                <th
                  key={mk}
                  scope="col"
                  className={`px-3 py-2 text-xs font-semibold text-gray-600 dark:text-slate-300 text-right whitespace-nowrap border-b border-gray-200 dark:border-[#374151] min-w-[100px] ${
                    onTableHeaderMonthClick
                      ? "cursor-pointer hover:bg-sky-100/50 dark:hover:bg-[#374151] hover:underline select-none"
                      : ""
                  }`}
                  title={onTableHeaderMonthClick ? "Ver todos os lançamentos deste mês" : undefined}
                  onClick={onTableHeaderMonthClick ? () => onTableHeaderMonthClick(mk) : undefined}
                >
                  {formatMonthKey(mk)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="bg-gray-100/80 dark:bg-[#252f3f]">
              <td className="sticky left-0 z-10 bg-gray-100/90 dark:bg-[#252f3f] px-2 py-2 border-b border-gray-200 dark:border-[#374151]">
                <button
                  type="button"
                  onClick={() => setOpen((v) => !v)}
                  className="flex items-center gap-2 text-left font-semibold text-gray-900 dark:text-slate-100 w-full hover:text-sky-700 dark:hover:text-[#63B3ED]"
                >
                  <span className="text-gray-500 w-4 shrink-0" aria-hidden>
                    {open ? "▼" : "▶"}
                  </span>
                  {group.planGroup}
                </button>
              </td>
              {renderMonthCells(group.monthlyTotals, presentation, onPlanRowMonthClick)}
            </tr>
            {open && children}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const Demonstrativo: React.FC = () => {
  const { company } = useCompany();
  const companyId = company?.id ?? 0;
  const defaultPeriodRange = useMemo(() => competenceRangeFromPreset("past-3"), []);
  const [periodPreset, setPeriodPreset] = useState<DashboardCompetencePreset>("past-3");
  const [customFromMonth, setCustomFromMonth] = useState(() =>
    competenceDateToMonthValue(defaultPeriodRange.competenceDateFrom)
  );
  const [customToMonth, setCustomToMonth] = useState(() =>
    competenceDateToMonthValue(defaultPeriodRange.competenceDateTo)
  );
  const [customPeriodError, setCustomPeriodError] = useState<string | null>(null);
  const [data, setData] = useState<TransactionDashboardData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => new Set());
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(() => new Set());
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const [canScrollHorizontally, setCanScrollHorizontally] = useState(false);
  const [transactionModal, setTransactionModal] = useState<{
    title: string;
    transactions: DashboardTransaction[];
  } | null>(null);

  const { showToast } = useToaster();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTransaction, setEditingTransaction] =
    useState<TransactionAttributes | null>(null);
  const [createFormVariant, setCreateFormVariant] = useState<"input" | "output">("output");
  const [bankAccounts, setBankAccounts] = useState<BankAccountAttributes[]>([]);
  const [costCenters, setCostCenters] = useState<CostCenterAttributes[]>([]);
  const [modalSubcategories, setModalSubcategories] = useState<SubcategoryAttributes[]>([]);
  const [modalSubcategoriesLoading, setModalSubcategoriesLoading] = useState(false);
  const [editPrefetchLoading, setEditPrefetchLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const competenceRange = useMemo(() => {
    if (periodPreset === "custom") {
      return competenceRangeFromCustomMonths(customFromMonth, customToMonth);
    }
    return competenceRangeFromPreset(periodPreset);
  }, [periodPreset, customFromMonth, customToMonth]);

  const load = useCallback(async () => {
    if (companyId === 0) {
      setData(null);
      return;
    }
    if (!competenceRange) {
      setData(null);
      setError(customPeriodError ?? "Período de competência inválido");
      return;
    }
    setLoading(true);
    setError(null);
    const res = await transactionsApi.getDashboard(companyId, competenceRange);
    if (res.success && res.data) {
      setData(res.data);
    } else {
      setError(res.error ?? "Falha ao carregar demonstrativo");
      setData(null);
    }
    setLoading(false);
  }, [companyId, competenceRange, customPeriodError]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (companyId === 0) {
      setBankAccounts([]);
      setCostCenters([]);
      return;
    }
    let cancelled = false;
    void Promise.all([
      bankAccountsApi.getAll(companyId),
      costCentersApi.getAll(companyId),
    ]).then(([accRes, costRes]) => {
      if (cancelled) return;
      if (accRes.success && accRes.data) setBankAccounts(accRes.data);
      if (costRes.success && costRes.data) setCostCenters(costRes.data);
    });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  useEffect(() => {
    if (!showCreateForm || companyId === 0) {
      setModalSubcategories([]);
      return;
    }
    let cancelled = false;
    setModalSubcategoriesLoading(true);
    void subcategoriesApi.getAll(companyId, undefined, createFormVariant).then((res) => {
      if (!cancelled && res.success && res.data) setModalSubcategories(res.data);
      if (!cancelled) setModalSubcategoriesLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [showCreateForm, companyId, createFormVariant]);

  const handleEditTransactionFromDemonstrativo = useCallback(
    async (transactionId: number) => {
      setTransactionModal(null);
      setEditPrefetchLoading(true);
      try {
        const res = await transactionsApi.getById(transactionId);
        if (!res.success || !res.data) {
          showToast("error", res.error ?? "Não foi possível carregar o lançamento para edição.");
          return;
        }
        if (res.data.companyId !== companyId) {
          showToast("error", "Este lançamento não pertence à empresa selecionada.");
          return;
        }
        setEditingTransaction(res.data);
        setCreateFormVariant(res.data.type === "input" ? "input" : "output");
        setShowCreateForm(true);
      } catch {
        showToast("error", "Não foi possível carregar o lançamento para edição.");
      } finally {
        setEditPrefetchLoading(false);
      }
    },
    [companyId, showToast]
  );

  const handleEditModalClose = () => {
    setShowCreateForm(false);
    setEditingTransaction(null);
  };

  const handleEditModalSuccess = () => {
    void load();
    setShowCreateForm(false);
    setEditingTransaction(null);
  };

  useEffect(() => {
    setExpandedCategories(new Set());
  }, [data?.companyId, data?.range?.from, data?.range?.to, data?.monthKeys.join("|")]);

  const selectPeriodPreset = (preset: Exclude<DashboardCompetencePreset, "custom">) => {
    setCustomPeriodError(null);
    setPeriodPreset(preset);
    const range = competenceRangeFromPreset(preset);
    setCustomFromMonth(competenceDateToMonthValue(range.competenceDateFrom));
    setCustomToMonth(competenceDateToMonthValue(range.competenceDateTo));
  };

  const handleCustomFromMonthChange = (monthValue: string) => {
    setCustomPeriodError(null);
    setPeriodPreset("custom");
    setCustomFromMonth(monthValue);
  };

  const handleCustomToMonthChange = (monthValue: string) => {
    setCustomPeriodError(null);
    setPeriodPreset("custom");
    setCustomToMonth(monthValue);
  };

  useEffect(() => {
    if (periodPreset !== "custom") return;
    if (!customFromMonth.trim() || !customToMonth.trim()) {
      setCustomPeriodError("Selecione o mês inicial e final.");
      return;
    }
    if (!competenceRangeFromCustomMonths(customFromMonth, customToMonth)) {
      setCustomPeriodError("O mês inicial deve ser anterior ou igual ao mês final.");
      return;
    }
    setCustomPeriodError(null);
  }, [periodPreset, customFromMonth, customToMonth]);

  const monthKeys = data?.monthKeys ?? [];
  const rangeLabel = useMemo(() => {
    if (!data?.range?.from || !data?.range?.to) return "";
    const a = formatDate(data.range.from);
    const b = formatDate(data.range.to);
    return `${a} – ${b}`;
  }, [data?.range?.from, data?.range?.to]);

  /** Intervalo de competência (não muda ao rolar — as setas só rolam a tabela). */
  const monthRangeLabel = useMemo(() => {
    if (monthKeys.length === 0) return "";
    if (monthKeys.length === 1) return formatMonthKey(monthKeys[0]);
    return `${formatMonthKey(monthKeys[0])}\u2009–\u2009${formatMonthKey(monthKeys[monthKeys.length - 1])}`;
  }, [monthKeys]);

  const updateScrollability = useCallback(() => {
    const el = tableScrollRef.current;
    if (!el) {
      setCanScrollHorizontally(false);
      return;
    }
    setCanScrollHorizontally(el.scrollWidth > el.clientWidth + 1);
  }, []);

  useLayoutEffect(() => {
    updateScrollability();
    const el = tableScrollRef.current;
    if (!el) return;
    const onResize = () => updateScrollability();
    window.addEventListener("resize", onResize);
    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => updateScrollability())
        : null;
    ro?.observe(el);
    return () => {
      window.removeEventListener("resize", onResize);
      ro?.disconnect();
    };
  }, [data, monthKeys, updateScrollability]);

  /** Rola o **conteúdo** da grade no eixo X quando há mais meses do que cabem na tela. */
  const scrollTable = (dir: -1 | 1) => {
    const el = tableScrollRef.current;
    if (!el) return;
    const th = el.querySelector("thead th:nth-child(2)") as HTMLElement | null;
    const colW = th?.getBoundingClientRect().width ?? 160;
    el.scrollBy({ left: dir * Math.max(96, Math.round(colW)), behavior: "smooth" });
  };

  const toggleGroup = (planGroup: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(planGroup)) next.delete(planGroup);
      else next.add(planGroup);
      return next;
    });
  };

  const toggleCategory = (key: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleExportXlsx = async () => {
    if (!data) return;
    setExportLoading(true);
    try {
      const { buildDemonstrativoXlsxBuffer } = await import(
        "../../../utils/demonstrativoExcelExport"
      );
      const buffer = await buildDemonstrativoXlsxBuffer(data);
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `demonstrativo-${data.range.from}_${data.range.to}-empresa-${data.companyId}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      showToast("success", "Arquivo XLSX gerado (Demonstrativo + Lançamentos).");
    } catch {
      showToast("error", "Não foi possível gerar o Excel. Tente novamente.");
    } finally {
      setExportLoading(false);
    }
  };

  const derived = useMemo(() => (data ? computeDerivedDashboard(data) : null), [data]);

  const renderMonthCells = (
    totals: { month: string; totalAmount: number }[],
    presentation: "input" | "output",
    onMonthClick?: (monthKey: string) => void
  ) =>
    monthKeys.map((mk) => {
      const v = totalForMonth(totals, mk);
      const { text, className } = formatSignedCell(v, presentation);
      const interactive = onMonthClick != null;
      return (
        <td
          key={mk}
          role={interactive ? "button" : undefined}
          tabIndex={interactive ? 0 : undefined}
          title={interactive ? "Ver lançamentos deste mês" : undefined}
          onClick={interactive ? () => onMonthClick(mk) : undefined}
          onKeyDown={
            interactive
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onMonthClick(mk);
                  }
                }
              : undefined
          }
          className={`px-3 py-2 text-right text-sm tabular-nums whitespace-nowrap border-b border-gray-100 dark:border-[#374151] ${className} ${
            interactive
              ? "cursor-pointer hover:bg-sky-100/70 dark:hover:bg-[#374151] focus:outline-none focus:ring-2 focus:ring-sky-500/40"
              : ""
          }`}
        >
          {text}
        </td>
      );
    });

  const renderGroupCategorySection = (
    group: DashboardGroupRow,
    presentation: "input" | "output"
  ): React.ReactNode[] =>
    group.categories.map((cat: DashboardCategoryRow) => {
      const catKey = `${group.planGroup}::${cat.categoryName}`;
      const hasSubs = Boolean(cat.subcategories?.length);

      if (!hasSubs) {
        return (
          <tr
            key={catKey}
            className="hover:bg-sky-50/50 dark:hover:bg-[#2D3748]/80"
          >
            <td className="sticky left-0 z-10 bg-white dark:bg-[#1A202C] px-2 py-2 pl-10 border-b border-gray-100 dark:border-[#374151] text-sm text-gray-800 dark:text-slate-200">
              <button
                type="button"
                className="text-left w-full hover:text-sky-700 dark:hover:text-[#63B3ED] cursor-pointer"
                onClick={() =>
                  setTransactionModal({
                    title: `${group.planGroup} › ${cat.categoryName}`,
                    transactions: getCategoryTransactions(cat),
                  })
                }
              >
                {cat.categoryName}
              </button>
            </td>
            {renderMonthCells(cat.monthlyTotals, presentation, (monthKey) => {
              const list = transactionsForCategoryMonth(cat, monthKey);
              setTransactionModal({
                title: `${group.planGroup} › ${cat.categoryName} · ${formatMonthKey(monthKey)}`,
                transactions: list,
              });
            })}
          </tr>
        );
      }

      const catOpen = expandedCategories.has(catKey);
      return (
        <React.Fragment key={catKey}>
          <tr className="hover:bg-sky-50/50 dark:hover:bg-[#2D3748]/80">
            <td className="sticky left-0 z-10 bg-white dark:bg-[#1A202C] px-2 py-2 pl-6 border-b border-gray-100 dark:border-[#374151] text-sm text-gray-800 dark:text-slate-200">
              <div className="flex items-center gap-1 min-w-0">
                <button
                  type="button"
                  onClick={() => toggleCategory(catKey)}
                  className="flex items-center justify-center w-7 shrink-0 text-gray-500 hover:text-sky-700 dark:hover:text-[#63B3ED]"
                  aria-expanded={catOpen}
                  aria-label={catOpen ? "Recolher subcategorias" : "Expandir subcategorias"}
                >
                  <span className="w-4 text-center" aria-hidden>
                    {catOpen ? "▼" : "▶"}
                  </span>
                </button>
                <button
                  type="button"
                  className="text-left flex-1 min-w-0 hover:text-sky-700 dark:hover:text-[#63B3ED] cursor-pointer truncate"
                  onClick={() =>
                    setTransactionModal({
                      title: `${group.planGroup} › ${cat.categoryName}`,
                      transactions: getCategoryTransactions(cat),
                    })
                  }
                >
                  {cat.categoryName}
                </button>
              </div>
            </td>
            {renderMonthCells(cat.monthlyTotals, presentation, (monthKey) => {
              setTransactionModal({
                title: `${group.planGroup} › ${cat.categoryName} · ${formatMonthKey(monthKey)}`,
                transactions: transactionsForCategoryMonth(cat, monthKey),
              });
            })}
          </tr>
          {catOpen &&
            (cat.subcategories ?? []).map((sub) => {
              const subKey = `${catKey}::${sub.subcategoryId}`;
              return (
                <tr
                  key={subKey}
                  className="hover:bg-sky-50/40 dark:hover:bg-[#2D3748]/60"
                >
                  <td className="sticky left-0 z-10 bg-white dark:bg-[#1A202C] px-2 py-2 pl-12 border-b border-gray-100 dark:border-[#374151] text-sm text-gray-800 dark:text-slate-200">
                    <button
                      type="button"
                      className="text-left w-full hover:text-sky-700 dark:hover:text-[#63B3ED] cursor-pointer truncate"
                      onClick={() =>
                        setTransactionModal({
                          title: `${group.planGroup} › ${cat.categoryName} › ${sub.subcategoryName}`,
                          transactions: sub.transactions ?? [],
                        })
                      }
                    >
                      {sub.subcategoryName}
                    </button>
                  </td>
                  {renderMonthCells(sub.monthlyTotals, presentation, (monthKey) => {
                    setTransactionModal({
                      title: `${group.planGroup} › ${cat.categoryName} › ${sub.subcategoryName} · ${formatMonthKey(monthKey)}`,
                      transactions: transactionsForSubcategoryMonth(sub, monthKey),
                    });
                  })}
                </tr>
              );
            })}
        </React.Fragment>
      );
    });

  const renderPlanGroupBlock = (group: DashboardGroupRow) => {
    const open = expandedGroups.has(group.planGroup);
    const presentation: "input" | "output" = isEntradasPlan(group.planGroup) ? "input" : "output";
    return (
      <React.Fragment key={group.planGroup}>
        <tr className="bg-gray-100/80 dark:bg-[#252f3f]">
          <td className="sticky left-0 z-10 bg-gray-100/90 dark:bg-[#252f3f] px-2 py-2 border-b border-gray-200 dark:border-[#374151]">
            <button
              type="button"
              onClick={() => toggleGroup(group.planGroup)}
              className="flex items-center gap-2 text-left font-semibold text-gray-900 dark:text-slate-100 w-full hover:text-sky-700 dark:hover:text-[#63B3ED]"
            >
              <span className="text-gray-500 w-4 shrink-0" aria-hidden>
                {open ? "▼" : "▶"}
              </span>
              {group.planGroup}
            </button>
          </td>
          {renderMonthCells(group.monthlyTotals, presentation, (monthKey) => {
            const list = transactionsForGroupMonth(group, monthKey);
            setTransactionModal({
              title: `${group.planGroup} · ${formatMonthKey(monthKey)}`,
              transactions: list,
            });
          })}
        </tr>
        {open && renderGroupCategorySection(group, presentation)}
      </React.Fragment>
    );
  };

  const periodButtonClass = (active: boolean) =>
    `px-3 py-1.5 font-medium transition-colors ${
      active
        ? "bg-sky-600 text-white"
        : "bg-white dark:bg-[#2D3748] text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-[#374151]"
    }`;

  const periodToolbar = (
    <div className="rounded-xl border border-gray-200 dark:border-[#374151] bg-white dark:bg-[#1A202C] shadow-sm px-4 sm:px-5 py-3 space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs sm:text-sm text-gray-600 dark:text-slate-400">
          <span className="font-medium text-gray-800 dark:text-slate-200">Período (data de competência)</span>
          {rangeLabel ? (
            <span className="block sm:inline sm:ml-2 text-gray-500 dark:text-slate-400">· {rangeLabel}</span>
          ) : null}
        </p>
        <p className="text-[11px] text-gray-500 dark:text-slate-400">
          Presets em meses relativos ao mês atual (passado e futuro).
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <div
            className="flex rounded-lg border border-gray-200 dark:border-[#4B5563] overflow-hidden text-sm shrink-0"
            role="radiogroup"
            aria-label="Período passado"
          >
            {DASHBOARD_PERIOD_PRESETS.slice(0, 3).map((preset) => (
              <button
                key={preset.id}
                type="button"
                role="radio"
                aria-checked={periodPreset === preset.id}
                onClick={() => selectPeriodPreset(preset.id)}
                className={periodButtonClass(periodPreset === preset.id)}
                title={`${preset.label} meses até o mês atual`}
              >
                {preset.label}
              </button>
            ))}
          </div>

          <div
            className="flex rounded-lg border border-gray-200 dark:border-[#4B5563] overflow-hidden text-sm shrink-0"
            role="radiogroup"
            aria-label="Período passado e futuro"
          >
            {DASHBOARD_PERIOD_PRESETS.slice(3).map((preset) => (
              <button
                key={preset.id}
                type="button"
                role="radio"
                aria-checked={periodPreset === preset.id}
                onClick={() => selectPeriodPreset(preset.id)}
                className={periodButtonClass(periodPreset === preset.id)}
                title={`${preset.label} meses em relação ao mês atual`}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-2 ml-auto justify-end">
          <button
            type="button"
            role="radio"
            aria-checked={periodPreset === "custom"}
            onClick={() => setPeriodPreset("custom")}
            className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
              periodPreset === "custom"
                ? "border-sky-600 bg-sky-600 text-white"
                : "border-gray-200 dark:border-[#4B5563] bg-white dark:bg-[#2D3748] text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-[#374151]"
            }`}
          >
            Personalizado
          </button>
          <div className="min-w-[132px]">
            <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-0.5">
              De (mês)
            </label>
            <Input
              type="month"
              value={customFromMonth}
              onChange={(e) => handleCustomFromMonthChange(e.target.value)}
              disabled={loading}
              className="text-sm"
            />
          </div>
          <div className="min-w-[132px]">
            <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-0.5">
              Até (mês)
            </label>
            <Input
              type="month"
              value={customToMonth}
              onChange={(e) => handleCustomToMonthChange(e.target.value)}
              disabled={loading}
              className="text-sm"
            />
          </div>
        </div>
      </div>
      {customPeriodError && periodPreset === "custom" ? (
        <p className="text-xs text-red-600 dark:text-red-400 text-right">{customPeriodError}</p>
      ) : null}
    </div>
  );

  const lucroGerencialToolbarActions = (
    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
      <div
        className="flex flex-col items-stretch gap-0.5 rounded-lg border border-gray-200 dark:border-[#4B5563] bg-white/80 dark:bg-[#2D3748] px-1 py-0.5"
        title={
          canScrollHorizontally
            ? "Rolar a grade na horizontal para ver os meses que estão fora da tela"
            : "Todos os meses já cabem na tela; não há rolagem horizontal"
        }
      >
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Rolar grade para a esquerda"
            disabled={!data || !canScrollHorizontally}
            className="p-2 text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-[#374151] rounded-md disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent dark:disabled:hover:bg-transparent"
            onClick={() => scrollTable(-1)}
          >
            ‹
          </button>
          <span className="px-1 text-xs font-semibold text-gray-800 dark:text-slate-100 min-w-[5rem] text-center leading-tight">
            {monthRangeLabel || "—"}
          </span>
          <button
            type="button"
            aria-label="Rolar grade para a direita"
            disabled={!data || !canScrollHorizontally}
            className="p-2 text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-[#374151] rounded-md disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent dark:disabled:hover:bg-transparent"
            onClick={() => scrollTable(1)}
          >
            ›
          </button>
        </div>
        <span className="text-[10px] text-center text-gray-500 dark:text-slate-400 px-1 pb-0.5 leading-tight">
          {canScrollHorizontally ? "Rolar meses" : "Meses visíveis"}
        </span>
      </div>
      <ActionButton
        type="button"
        variant="outline"
        size="sm"
        icon={<Icons.Import />}
        onClick={() => void handleExportXlsx()}
        disabled={!data || loading || exportLoading}
        loading={exportLoading}
        className="dark:border-[#4B5563] dark:bg-[#374151] dark:text-slate-200"
        tooltip="Exportar Excel (.xlsx): demonstrativo colorido + todos os lançamentos"
      >
        Exportar
      </ActionButton>
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in max-w-[100vw]">
      {periodToolbar}

      <section className="rounded-2xl border border-gray-200 dark:border-[#374151] bg-white dark:bg-[#1A202C] shadow-sm overflow-hidden">
        <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-4 sm:px-5 py-3 border-b border-gray-200 dark:border-[#374151] bg-gradient-to-r from-sky-50/90 to-blue-50/60 dark:from-[#1e3a5f]/90 dark:to-[#1A202C]">
          <h2 className="text-lg sm:text-xl font-semibold text-sky-700 dark:text-[#63B3ED]">Lucro Gerencial</h2>
          {lucroGerencialToolbarActions}
        </header>

        {error && (
          <div className="p-4">
            <ErrorState message={error} variant="card" onRetry={() => void load()} />
          </div>
        )}

        {loading && !data ? (
          <div className="p-12">
            <Loading message="Carregando demonstrativo..." size="lg" />
          </div>
        ) : data ? (
          <div ref={tableScrollRef} className="overflow-x-auto">
            <table className="min-w-max w-full border-collapse text-left">
              <thead>
                <tr className="bg-gray-50 dark:bg-[#2D3748]">
                  <th className="sticky left-0 z-10 bg-gray-50 dark:bg-[#2D3748] px-3 py-2 text-xs font-semibold text-gray-600 dark:text-slate-300 border-b border-gray-200 dark:border-[#374151] min-w-[220px]">
                    Plano / Categoria
                  </th>
                  {monthKeys.map((mk) => (
                    <th
                      key={mk}
                      scope="col"
                      className="px-3 py-2 text-xs font-semibold text-gray-600 dark:text-slate-300 text-right whitespace-nowrap border-b border-gray-200 dark:border-[#374151] min-w-[100px] cursor-pointer hover:bg-sky-100/50 dark:hover:bg-[#374151] hover:underline select-none"
                      title="Ver todos os lançamentos deste mês"
                      onClick={() => {
                        if (!data) return;
                        const list = transactionsForMonthInDashboard(data, mk);
                        setTransactionModal({
                          title: `Competência ${formatMonthKey(mk)}`,
                          transactions: list,
                        });
                      }}
                    >
                      {formatMonthKey(mk)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {derived &&
                  derived.preMarginRows.map((group) => renderPlanGroupBlock(group))}
                {derived && (
                  <tr className="border-t-2 border-gray-300 dark:border-slate-600 bg-sky-50/40 dark:bg-[#243447]">
                    <td className="sticky left-0 z-10 bg-sky-50/90 dark:bg-[#243447] px-3 py-2.5 border-b border-gray-200 dark:border-[#374151] text-sm font-bold text-gray-900 dark:text-slate-100">
                      Margem de Contribuição
                      <span className="block text-[11px] font-normal text-gray-500 dark:text-slate-400 mt-0.5">
                        Entradas − Impostos − Custos e Despesas Variáveis
                      </span>
                    </td>
                    {renderSummaryMonthCells(monthKeys, derived.marginMonthly, derived.entradasMonthly)}
                  </tr>
                )}
                {derived && derived.otherAfterMargin.map((group) => renderPlanGroupBlock(group))}
                {derived && derived.fixedExpenseGroups.map((group) => renderPlanGroupBlock(group))}
                {derived && (
                  <tr className="border-t-2 border-gray-300 dark:border-slate-600 bg-sky-50/40 dark:bg-[#243447]">
                    <td className="sticky left-0 z-10 bg-sky-50/90 dark:bg-[#243447] px-3 py-2.5 border-b border-gray-200 dark:border-[#374151] text-sm font-bold text-gray-900 dark:text-slate-100">
                      Lucro Gerencial
                      <span className="block text-[11px] font-normal text-gray-500 dark:text-slate-400 mt-0.5">
                        Margem de Contribuição − Despesas Fixas
                      </span>
                    </td>
                    {renderSummaryMonthCells(monthKeys, derived.lucroMonthly, derived.entradasMonthly)}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="p-8 text-sm text-gray-500 dark:text-slate-400">Selecione uma empresa.</p>
        )}
      </section>

      {data && derived && derived.valoresPessoaisPlans.length > 0 ? (
        <section className="rounded-2xl border border-gray-200 dark:border-[#374151] bg-white dark:bg-[#1A202C] shadow-sm overflow-hidden">
          <header className="px-4 sm:px-5 py-3 border-b border-gray-200 dark:border-[#374151] bg-gradient-to-r from-sky-50/90 to-blue-50/60 dark:from-[#1e3a5f]/90 dark:to-[#1A202C]">
            <h2 className="text-base sm:text-lg font-semibold text-sky-700 dark:text-[#63B3ED] leading-snug">
              Valores Pessoais: Pró-Labore, Distribuição de Lucros e Movimentações Pessoais
            </h2>
          </header>
          <div className="p-4 sm:p-5 space-y-4">
          {derived.valoresPessoaisPlans.map((group) => {
            const presentation: "input" | "output" = isEntradasPlan(group.planGroup) ? "input" : "output";
            return (
              <DemonstrativoSeparatePlanCard
                key={group.planGroup}
                group={group}
                monthKeys={monthKeys}
                presentation={presentation}
                renderMonthCells={renderMonthCells}
                onTableHeaderMonthClick={
                  data
                    ? (mk) => {
                        setTransactionModal({
                          title: `Competência ${formatMonthKey(mk)}`,
                          transactions: transactionsForMonthInDashboard(data, mk),
                        });
                      }
                    : undefined
                }
                onPlanRowMonthClick={(mk) => {
                  setTransactionModal({
                    title: `${group.planGroup} · ${formatMonthKey(mk)}`,
                    transactions: transactionsForGroupMonth(group, mk),
                  });
                }}
              >
                {renderGroupCategorySection(group, presentation)}
              </DemonstrativoSeparatePlanCard>
            );
          })}
          </div>
        </section>
      ) : null}

      {data && derived && derived.investimentosFinanciamentosPlans.length > 0 ? (
        <section className="rounded-2xl border border-gray-200 dark:border-[#374151] bg-white dark:bg-[#1A202C] shadow-sm overflow-hidden">
          <header className="px-4 sm:px-5 py-3 border-b border-gray-200 dark:border-[#374151] bg-gradient-to-r from-sky-50/90 to-blue-50/60 dark:from-[#1e3a5f]/90 dark:to-[#1A202C]">
            <h2 className="text-base sm:text-lg font-semibold text-sky-700 dark:text-[#63B3ED] leading-snug">
              Investimentos, Empréstimos e Financiamentos
            </h2>
          </header>
          <div className="p-4 sm:p-5 space-y-4">
          {derived.investimentosFinanciamentosPlans.map((group) => {
            const presentation: "input" | "output" = isEntradasPlan(group.planGroup) ? "input" : "output";
            return (
              <DemonstrativoSeparatePlanCard
                key={group.planGroup}
                group={group}
                monthKeys={monthKeys}
                presentation={presentation}
                renderMonthCells={renderMonthCells}
                onTableHeaderMonthClick={
                  data
                    ? (mk) => {
                        setTransactionModal({
                          title: `Competência ${formatMonthKey(mk)}`,
                          transactions: transactionsForMonthInDashboard(data, mk),
                        });
                      }
                    : undefined
                }
                onPlanRowMonthClick={(mk) => {
                  setTransactionModal({
                    title: `${group.planGroup} · ${formatMonthKey(mk)}`,
                    transactions: transactionsForGroupMonth(group, mk),
                  });
                }}
              >
                {renderGroupCategorySection(group, presentation)}
              </DemonstrativoSeparatePlanCard>
            );
          })}
          </div>
        </section>
      ) : null}

      {data && derived ? (
        <section className="rounded-2xl border border-gray-200 dark:border-[#374151] bg-white dark:bg-[#1A202C] shadow-sm overflow-hidden">
          <header className="px-4 sm:px-5 py-3 border-b border-gray-200 dark:border-[#374151] bg-gradient-to-r from-sky-50/90 to-blue-50/60 dark:from-[#1e3a5f]/90 dark:to-[#1A202C]">
            <h2 className="text-base sm:text-lg font-semibold text-sky-700 dark:text-[#63B3ED] leading-snug">
              Geração de Reserva
            </h2>
            <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">
              Lucro Gerencial − Valores Pessoais − Investimentos
            </p>
          </header>
          <div className="overflow-x-auto">
            <table className="min-w-max w-full border-collapse text-left">
              <thead>
                <tr className="bg-gray-50 dark:bg-[#2D3748]">
                  <th className="sticky left-0 z-10 bg-gray-50 dark:bg-[#2D3748] px-3 py-2 text-xs font-semibold text-gray-600 dark:text-slate-300 border-b border-gray-200 dark:border-[#374151] min-w-[220px]">
                    Resumo
                  </th>
                  {monthKeys.map((mk) => (
                    <th
                      key={mk}
                      scope="col"
                      className="px-3 py-2 text-xs font-semibold text-gray-600 dark:text-slate-300 text-right whitespace-nowrap border-b border-gray-200 dark:border-[#374151] min-w-[100px] cursor-pointer hover:bg-sky-100/50 dark:hover:bg-[#374151] hover:underline select-none"
                      title="Ver todos os lançamentos deste mês"
                      onClick={() => {
                        const list = transactionsForMonthInDashboard(data, mk);
                        setTransactionModal({
                          title: `Competência ${formatMonthKey(mk)}`,
                          transactions: list,
                        });
                      }}
                    >
                      {formatMonthKey(mk)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="hover:bg-sky-50/30 dark:hover:bg-[#2D3748]/40">
                  <td className="sticky left-0 z-10 bg-white dark:bg-[#1A202C] px-3 py-2 border-b border-gray-100 dark:border-[#374151] text-sm text-gray-800 dark:text-slate-200">
                    Lucro Gerencial
                  </td>
                  {monthKeys.map((mk, i) => {
                    const v = derived.lucroMonthly[i] ?? 0;
                    const { text, className } = formatSignedCell(v, "net");
                    return (
                      <td
                        key={mk}
                        className={`px-3 py-2 text-right text-sm tabular-nums whitespace-nowrap border-b border-gray-100 dark:border-[#374151] ${className}`}
                      >
                        {text}
                      </td>
                    );
                  })}
                </tr>
                <tr className="hover:bg-sky-50/30 dark:hover:bg-[#2D3748]/40">
                  <td className="sticky left-0 z-10 bg-white dark:bg-[#1A202C] px-3 py-2 border-b border-gray-100 dark:border-[#374151] text-sm text-gray-800 dark:text-slate-200">
                    Total Valores Pessoais
                  </td>
                  {monthKeys.map((mk, i) => {
                    const v = derived.valoresPessoaisMonthly[i] ?? 0;
                    const { text, className } = formatSignedCell(v, "output");
                    return (
                      <td
                        key={mk}
                        className={`px-3 py-2 text-right text-sm tabular-nums whitespace-nowrap border-b border-gray-100 dark:border-[#374151] ${className}`}
                      >
                        {text}
                      </td>
                    );
                  })}
                </tr>
                <tr className="hover:bg-sky-50/30 dark:hover:bg-[#2D3748]/40">
                  <td className="sticky left-0 z-10 bg-white dark:bg-[#1A202C] px-3 py-2 border-b border-gray-100 dark:border-[#374151] text-sm text-gray-800 dark:text-slate-200">
                    Total Inv/Emp/Financia.
                  </td>
                  {monthKeys.map((mk, i) => {
                    const v = derived.investimentosFinanciamentosMonthly[i] ?? 0;
                    const { text, className } = formatSignedCell(v, "output");
                    return (
                      <td
                        key={mk}
                        className={`px-3 py-2 text-right text-sm tabular-nums whitespace-nowrap border-b border-gray-100 dark:border-[#374151] ${className}`}
                      >
                        {text}
                      </td>
                    );
                  })}
                </tr>
                <tr className="border-t-2 border-gray-300 dark:border-slate-600 bg-gray-50/80 dark:bg-[#252f3f]/90">
                  <td className="sticky left-0 z-10 bg-gray-50/95 dark:bg-[#252f3f] px-3 py-2.5 text-sm font-bold text-gray-900 dark:text-slate-100 border-b border-gray-200 dark:border-[#374151]">
                    Geração de Reserva
                  </td>
                  {monthKeys.map((mk, i) => {
                    const v = derived.geracaoReservaMonthly[i] ?? 0;
                    const { text, className } = formatSignedCell(v, "net");
                    return (
                      <td
                        key={mk}
                        className={`px-3 py-2.5 text-right text-sm font-bold tabular-nums whitespace-nowrap border-b border-gray-200 dark:border-[#374151] ${className}`}
                      >
                        {text}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <CreateInputTransactionModal
        isOpen={showCreateForm && createFormVariant === "input"}
        onClose={handleEditModalClose}
        onSuccess={handleEditModalSuccess}
        companyId={companyId}
        subcategories={createFormVariant === "input" ? modalSubcategories : []}
        subcategoriesLoading={createFormVariant === "input" && modalSubcategoriesLoading}
        bankAccounts={bankAccounts}
        costCenters={costCenters}
        editingTransaction={createFormVariant === "input" ? editingTransaction : null}
      />
      <CreateTransactionModal
        isOpen={showCreateForm && createFormVariant === "output"}
        onClose={handleEditModalClose}
        onSuccess={handleEditModalSuccess}
        companyId={companyId}
        subcategories={createFormVariant === "output" ? modalSubcategories : []}
        subcategoriesLoading={createFormVariant === "output" && modalSubcategoriesLoading}
        bankAccounts={bankAccounts}
        costCenters={costCenters}
        editingTransaction={createFormVariant === "output" ? editingTransaction : null}
      />

      <Modal
        isOpen={transactionModal != null}
        onClose={() => setTransactionModal(null)}
        title={transactionModal?.title ?? ""}
        icon={<IconsLarge.Dashboard />}
        size="lg"
        headerGradient="blue"
      >
        {transactionModal && (
          <CategoryTransactionsDetail
            transactions={transactionModal.transactions}
            onClose={() => setTransactionModal(null)}
            onEditTransaction={handleEditTransactionFromDemonstrativo}
            editPrefetchLoading={editPrefetchLoading}
          />
        )}
      </Modal>
    </div>
  );
};

const CategoryTransactionsDetail: React.FC<{
  transactions: DashboardTransaction[];
  onClose: () => void;
  onEditTransaction: (transactionId: number) => void;
  editPrefetchLoading: boolean;
}> = ({ transactions, onClose, onEditTransaction, editPrefetchLoading }) => {
  if (transactions.length === 0) {
    return (
      <p className="text-sm text-gray-600 dark:text-slate-400">Nenhum lançamento nesta categoria.</p>
    );
  }
  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500 dark:text-slate-400">
        {transactions.length} lançamento{transactions.length !== 1 ? "s" : ""} (competência). Clique em
        uma linha para editar (carrega por ID).
      </p>
      <div className="overflow-x-auto max-h-[60vh] overflow-y-auto rounded-lg border border-gray-200 dark:border-[#374151]">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-[#2D3748] sticky top-0">
            <tr>
              <th
                className="w-14 px-2 py-2 font-semibold text-gray-600 dark:text-slate-300 text-center"
                aria-label="Parcelamento e recorrência"
                title="Parcelamento e recorrência"
              />
              <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-slate-300">Competência</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-slate-300">Data pag.</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-slate-300 min-w-[140px]">
                Subcategoria
              </th>
              <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-slate-300">Descrição</th>
              <th className="text-right px-3 py-2 font-semibold text-gray-600 dark:text-slate-300">Valor</th>
            </tr>
          </thead>
          <tbody>
            {transactions.map((t) => (
              <tr
                key={t.id}
                role="button"
                tabIndex={0}
                title="Clique para editar"
                className={`border-t border-gray-100 dark:border-[#374151] ${
                  editPrefetchLoading
                    ? "opacity-60 cursor-wait"
                    : "cursor-pointer hover:bg-sky-50/60 dark:hover:bg-[#2a3444]/80"
                }`}
                onClick={() => {
                  if (!editPrefetchLoading) onEditTransaction(t.id);
                }}
                onKeyDown={(e) => {
                  if (editPrefetchLoading) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onEditTransaction(t.id);
                  }
                }}
              >
                <td className="px-2 py-2 text-center align-middle">
                  <span className="inline-flex items-center justify-center gap-1">
                    {t.installment?.trim() ? (
                      <span
                        className="inline-flex text-amber-600 dark:text-amber-400"
                        title={`Parcelamento (${t.installment.trim()})`}
                        aria-label="Transação parcelada"
                      >
                        <Icons.Installment />
                      </span>
                    ) : null}
                    {t.recurrencyId != null && t.recurrencyId !== 0 ? (
                      <span
                        className="inline-flex text-sky-600 dark:text-[#63B3ED]"
                        title={`Recorrência (grupo #${t.recurrencyId})`}
                        aria-label="Transação de recorrência"
                      >
                        <Icons.Recurrency />
                      </span>
                    ) : null}
                  </span>
                </td>
                <td className="px-3 py-2 text-gray-600 dark:text-slate-400 whitespace-nowrap">
                  {formatDate(t.competenceDate)}
                </td>
                <td className="px-3 py-2 text-gray-600 dark:text-slate-400 whitespace-nowrap">
                  {formatDate(t.paymentDate)}
                </td>
                <td className="px-3 py-2 text-gray-700 dark:text-slate-300 whitespace-nowrap max-w-[200px] truncate" title={t.subcategory?.name ?? undefined}>
                  {t.subcategory?.name ?? "—"}
                </td>
                <td className="px-3 py-2 text-gray-900 dark:text-slate-100">{t.description || "—"}</td>
                <td
                  className={`px-3 py-2 text-right font-medium tabular-nums ${
                    t.type === "input"
                      ? "text-emerald-600 dark:text-emerald-400"
                      : "text-red-600 dark:text-red-400"
                  }`}
                >
                  {t.type === "input" ? "+" : "−"}
                  {formatCurrency(t.amount)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-end">
        <ActionButton type="button" variant="outline" size="sm" onClick={onClose}>
          Fechar
        </ActionButton>
      </div>
    </div>
  );
};
